import styled, { css } from 'styled-components';

import textStyles from 'styles/theme/textStyles';

export type SegmentedControlOption = {
  label: string;
  value: string;
  testId?: string;
};

type SegmentedControlProps = {
  options: SegmentedControlOption[];
  selectedValue: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  // In mobile, stretch the control to fill its row and split the width evenly across options.
  mobileFullWidth?: boolean;
  // Stretch the control to fill its row and split the width evenly across options, at every
  // width — not gated to mobile like mobileFullWidth.
  fullWidth?: boolean;
};

export default function SegmentedControl({
  options,
  selectedValue,
  onChange,
  disabled = false,
  mobileFullWidth = false,
  fullWidth = false,
}: SegmentedControlProps) {
  return (
    <Container $mobileFullWidth={mobileFullWidth} $fullWidth={fullWidth}>
      {options.map((option) => {
        const isSelected = option.value === selectedValue;
        return (
          <Segment
            key={option.value}
            $selected={isSelected}
            $mobileFullWidth={mobileFullWidth}
            $fullWidth={fullWidth}
            disabled={disabled}
            data-testid={option.testId}
            data-active={isSelected}
            onClick={() => {
              if (!disabled) onChange(option.value);
            }}>
            {option.label}
          </Segment>
        );
      })}
    </Container>
  );
}

const Container = styled.div<{ $mobileFullWidth?: boolean; $fullWidth?: boolean }>`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.white};
  height: 40px;
  padding: 0 4px;
  gap: 2px;

  ${({ $mobileFullWidth }) =>
    $mobileFullWidth &&
    css`
      @media (max-width: 800px) {
        display: flex;
        width: 100%;
      }
    `}

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      display: flex;
      width: 100%;
    `}
`;

const Segment = styled.button<{ $selected: boolean; $mobileFullWidth?: boolean; $fullWidth?: boolean }>`
  padding: 0 16px;
  height: 32px;
  font-size: 12px;
  font-weight: ${({ $selected }) => ($selected ? 500 : 400)};
  font-family: ${textStyles.base.fontFamily};
  border: none;
  border-radius: 3px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  white-space: nowrap;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  background-color: ${({ $selected, theme }) => ($selected ? theme.colors.blue10 : 'transparent')};
  color: ${({ $selected, theme }) => ($selected ? theme.colors.blue100 : theme.colors.black100)};

  ${({ $mobileFullWidth }) =>
    $mobileFullWidth &&
    css`
      @media (max-width: 800px) {
        flex: 1 0 auto;
        padding: 0 8px;
      }
    `}

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      flex: 1 0 auto;
      padding: 0 8px;
    `}
`;
